import { MutationFunctionOptions } from '@apollo/client';
import { ExecutionResult } from 'graphql';

import { ModalModel } from '@notino/react-styleguide';

import { RequestState } from '@containers/App/model';

import { IAddToCartButtonProps, PostAddToCartAction } from '../model';
import {
  AddProductToCart,
  AddProductToCartVariables,
} from '../queries/__generated__/AddProductToCart';
import {
  CartAddProductWithService,
  CartAddProductWithServiceVariables,
} from '../queries/__generated__/CartAddProductWithService';
import { INotificationMessage } from '../utils';

export interface IAddToCartButtonState extends IAddToCartButtonDefaultState {
  statusMessages?: string[];
  showAddToCartModal: boolean;
  notificationHeader?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  statusMessagesValues?: any;
  product: IAddToCartButtonProps['product'];
  buttonWrapper: IAddToCartButtonProps['buttonWrapper'];
  buttonWrapperProps: IAddToCartButtonProps['buttonWrapperProps'];
  buttonElementId: IAddToCartButtonProps['buttonElementId'];
  buttonElementClassName: IAddToCartButtonProps['buttonElementClassName'];
  wrapable: IAddToCartButtonProps['wrapable'];
  buttonStyle: IAddToCartButtonProps['buttonStyle'];
  buttonSize: IAddToCartButtonProps['buttonSize'];
  buttonText: IAddToCartButtonProps['buttonText'];
  onProductAdded: IAddToCartButtonProps['onProductAdded'];
  handleGtmPush: IAddToCartButtonProps['handleGtmPush'];
  closingModal: IAddToCartButtonProps['closingModal'];
  disableRedirect: IAddToCartButtonProps['disableRedirect'];
  cartUrl: string;
  count: number;
  services: IAddToCartButtonProps['services'];
  trackAddToCartAsync: IAddToCartButtonProps['trackAddToCartAsync'];
  withLegacyAddToCart: IAddToCartButtonProps['withLegacyAddToCart'];
}

export interface IAddToCartButtonContext {
  actions: IActions;
  state: IAddToCartButtonState;
  apollo: IApollo;
}

export interface IApollo {
  mutations: IMutations;
}

export interface IMutations {
  cartAddToCartDeprecated: (
    options?: MutationFunctionOptions<
      CartAddProductWithService,
      CartAddProductWithServiceVariables
    >
  ) => Promise<void | ExecutionResult<CartAddProductWithService>>;
  addToCart: (
    options?: MutationFunctionOptions<
      AddProductToCart,
      AddProductToCartVariables
    >
  ) => Promise<ExecutionResult<AddProductToCart>>;
}

export interface IHandleProductAddedToCartParams {
  product: IAddToCartButtonProps['product'];
  count: IAddToCartButtonProps['count'];
  handleGtmPush: IAddToCartButtonProps['handleGtmPush'];
  result: IAddToCartResult;
  modal: IModal;
}
export interface IModal {
  content: JSX.Element;
  props: {
    center?: boolean;
    size?: ModalModel.Sizes;
    header?: JSX.Element | string;
    subHeader?: JSX.Element | string;
    onClose?: () => void;
    withFocusTrap?: boolean;
  };
}

interface IAddToCartResult {
  success?: boolean;
  cartSettings?: PostAddToCartAction;
  statusMessages?: IAddToCartButtonState['statusMessages'];
  notificationMessage?: INotificationMessage; //Note: legacy prop .. remove when only new addToCart is used
}

export interface IActions {
  handleCloseModal: (
    closingModal?: IAddToCartButtonProps['closingModal']
  ) => void;
  setMouseOverBuyButton: () => void;
  handleProductAddedToCart: (params: IHandleProductAddedToCartParams) => void;
  showSpecialModal: (modal: JSX.Element, header: string | JSX.Element) => void;
  handleIncreaseSubmitAttempts: () => void;
  handleFailedAddToCart: () => void;
  updateStateAfterPropsChange: (props: IPropsToState) => void;
}

export interface IAddToCartButtonDefaultState {
  sendState: RequestState;
  submitAttempts: number;
  mouseOverBuyButton: boolean;
}

export enum ActionTypes {
  HANDLE_CLOSE_MODAL = 'HANDLE_CLOSE_MODAL',
  SET_MOUSE_OVER_BUY_BUTTON = 'SET_MOUSE_OVER_BUY_BUTTON',
  HANDLE_PRODUCT_ADDED_TO_CART = 'HANDLE_PRODUCT_ADDED_TO_CART',
  INCREASE_SUBMIT_ATTEMPTS = 'INCREASE_SUBMIT_ATTEMPTS',
  HANDLE_FAILED_ADD_TO_CART = 'HANDLE_FAILED_ADD_TO_CART',
  HANDLE_SUCCESS_ADD_TO_CART = 'HANDLE_SUCCESS_ADD_TO_CART',
  UPDATE_STATE_AFTER_PROP_CHANGE = 'UPDATE_STATE_AFTER_PROP_CHANGE',
}

interface IOnAddedToCartPayload {
  failed?: boolean;
}

export interface IPropsToState {
  product: IAddToCartButtonState['product'];
  count: IAddToCartButtonState['count'];
  buttonWrapper: IAddToCartButtonState['buttonWrapper'];
  buttonWrapperProps: IAddToCartButtonState['buttonWrapperProps'];
  buttonElementId: IAddToCartButtonState['buttonElementId'];
  buttonElementClassName: IAddToCartButtonState['buttonElementClassName'];
  wrapable: IAddToCartButtonState['wrapable'];
  buttonStyle: IAddToCartButtonState['buttonStyle'];
  buttonSize: IAddToCartButtonState['buttonSize'];
  buttonText: IAddToCartButtonState['buttonText'];
  onProductAdded: IAddToCartButtonState['onProductAdded'];
  handleGtmPush: IAddToCartButtonState['handleGtmPush'];
  closingModal: IAddToCartButtonState['closingModal'];
  disableRedirect: IAddToCartButtonState['disableRedirect'];
  showAddToCartModal: IAddToCartButtonState['showAddToCartModal'];
  services: IAddToCartButtonState['services'];
  trackAddToCartAsync: IAddToCartButtonState['trackAddToCartAsync'];
  withLegacyAddToCart: IAddToCartButtonState['withLegacyAddToCart'];
}

export type Action =
  | { type: ActionTypes.HANDLE_CLOSE_MODAL }
  | { type: ActionTypes.SET_MOUSE_OVER_BUY_BUTTON }
  | {
      type: ActionTypes.HANDLE_PRODUCT_ADDED_TO_CART;
      payload: IOnAddedToCartPayload;
    }
  | { type: ActionTypes.INCREASE_SUBMIT_ATTEMPTS }
  | { type: ActionTypes.HANDLE_FAILED_ADD_TO_CART }
  | { type: ActionTypes.HANDLE_SUCCESS_ADD_TO_CART }
  | {
      type: ActionTypes.UPDATE_STATE_AFTER_PROP_CHANGE;
      payload: IPropsToState;
    };
